import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { plausibleEvent } from "../utils/track";

const POLL_MAX = 8;

const PaymentSuccess = () => {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState("checking");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!sessionId) { setState("failed"); return; }
    let tries = 0, cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          plausibleEvent("Purchase", { package: data.package_id || "" });
          const pkg = data.package_id || "";
          if (pkg.startsWith("boost") || pkg.startsWith("frontpage") || pkg.startsWith("reach") || pkg.startsWith("exclusive")) {
            plausibleEvent("Boost Purchased", { package: pkg });
          }
          refresh && refresh();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* transient */ }
      tries += 1;
      if (tries >= POLL_MAX) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/50 px-5">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl shadow-xl p-8 text-center" data-testid="payment-success">
        {state === "checking" && (<><Loader2 size={44} className="mx-auto text-[#16a34a] animate-spin mb-4" /><h1 className="text-xl font-extrabold text-foreground">{t("psx.confirmingYourPayment")}</h1><p className="text-sm text-muted-foreground mt-1">{t("psx.thisOnlyTakesA")}</p></>)}
        {state === "paid" && (<><CheckCircle2 size={52} className="mx-auto text-[#16a34a] mb-4" /><h1 className="text-2xl font-extrabold text-foreground">{t("psx.paymentSuccessful")}</h1><p className="text-sm text-muted-foreground mt-1 mb-6">{t("psx.yourUpgradeIsNow")}</p><Link to="/account" className="inline-block bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold px-6 py-2.5 rounded-xl transition-colors">{t("psx.goToMyAccount")}</Link></>)}
        {(state === "failed" || state === "timeout") && (<><XCircle size={52} className="mx-auto text-rose-500 mb-4" /><h1 className="text-xl font-extrabold text-foreground">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1><p className="text-sm text-muted-foreground mt-1 mb-6">{state === "timeout" ? "Your payment is taking longer than usual. It will apply automatically once confirmed." : "We couldn't confirm this payment. You have not been charged."}</p><Link to="/premium" className="inline-block bg-secondary hover:bg-[#e7f6ec] text-foreground font-semibold px-6 py-2.5 rounded-xl transition-colors">{t("psx.backToPremium")}</Link></>)}
      </div>
    </div>
  );
};

export default PaymentSuccess;
